// The gazetteer editor's list page, at /me/places: every stored TaskPogoPlace
// on one map. Tapping a place opens a popup with its whole row and a link to
// edit it; the map's "+" arms add-mode and the next tap creates a place there.
//
// This is the only place the shared place database is visible as a database.
// Everywhere else it is invisible machinery -- a name typed on a trip map
// resolves through it, and a correction feeds back into it, without anyone
// seeing a row (see src/utilities/places.mjs).
//
// Not item-scoped, and deliberately reachable from the cross-item Trips page
// rather than the top nav: it's a tool for the people editing routes, not a
// primary destination.

import { useCallback, useEffect, useState } from 'react';
import { Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { Link, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Spinner from 'react-bootstrap/Spinner';
import { CardHeader, DataCard, useStatusMessage } from '../../components';
import PlacesTable from './PlacesTable';
import NearbyPlacesModal from '../../components/NearbyPlacesModal';
// Named for what it is rather than `Places`, which is this page's own name.
import * as PlaceStore from '../../utilities/places.mjs';
import * as PlaceFields from '../../fleet-shared/PlaceFields.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { PLACE_LANE_PREFIX } from '../../utilities/saveLanes';
import { urlForAllTrips, urlForPlace, urlForPlaceVisits } from '../../fleet-shared/Urls.mjs';

// maplibre-gl is ~250 KB gz and must stay out of the entry bundle.
const PlacesMap = lazyWithReload(() => import('../../components/map/PlacesMap'), 'PlacesMap');

// The lanes the edit page writes on. Its key is derived from a row id this
// page has no way to know it touched, so it waits on the whole prefix.
// Re-exported for the callers that already import it from here.
export { PLACE_LANE_PREFIX };

const Places = () => {
  const navigate = useNavigate();
  const [StatusMessage, { showLoading, showText, showError, hideStatus }] = useStatusMessage();
  const [places, setPlaces] = useState(null);
  const [selected, setSelected] = useState(null);
  // The pending "there is already a place here" question: where the user
  // tapped, and what is already near it.
  const [nearby, setNearby] = useState(null);

  useEffect(() => {
    let mounted = true;
    // Lets the lane wait below be dropped on unmount instead of holding a
    // queue subscription for the life of the page.
    const leaving = new AbortController();

    async function load() {
      try {
        showLoading();

        // Browser Back from the edit page STARTS that page's flush on unmount
        // but doesn't finish it. A read issued now would return the pre-save
        // row, and nothing later would correct it. Read the lanes before the
        // first await -- a round trip ahead of the check lets the write land
        // and a stale answer read as current.
        saveQueue.flushAll();
        await saveQueue.settledAll(
          saveQueue.pendingKeys().filter((key) => key.startsWith(PLACE_LANE_PREFIX)),
          { signal: leaving.signal }
        );
        if (!mounted) return;

        const rows = await PlaceStore.loadAllPlaces();
        if (!mounted) return;

        setPlaces(rows);
        hideStatus();
      } catch (err) {
        if (mounted) showError(err);
      }
    }

    load();
    return () => { mounted = false; leaving.abort(); };
  }, [hideStatus, showError, showLoading]);

  const handleSelect = useCallback((id, lngLat = null) => {
    setSelected(id ? { id, lngLat } : null);
  }, []);

  const handleEdit = useCallback((id) => {
    navigate(urlForPlace(id));
  }, [navigate]);

  const handleVisits = useCallback((id) => {
    navigate(urlForPlaceVisits(id));
  }, [navigate]);

  // Create-then-edit, the same flow as adding a trip or an item: the stub row
  // gives the edit page a real id to autosave against. Its name selects on
  // focus (EditForm's handleFocus knows NewPlaceName), so typing over it is
  // one gesture.
  const createAt = useCallback(async (lat, lon) => {
    try {
      showText('Adding...');
      const created = await PlaceStore.createPlaceAt({ lat, lon });
      hideStatus();
      navigate(urlForPlace(created.id));
    } catch (err) {
      showError(err);
    }
  }, [hideStatus, navigate, showError, showText]);

  // A tap in add-mode that landed next to places that already exist asks
  // before it creates. PlacesMap already hit-tests the circle layer so add-mode
  // never drops a row ON an existing dot; this extends that guard from "on it"
  // to "next to it", which is the case a curator can't see at a zoomed-out
  // camera.
  //
  // No name is involved -- the stub is NewPlaceName -- so distance is the only
  // signal there is, and that is exactly why the check earns its place here.
  // `places` is already in state, so it costs one array walk and no request.
  const handleAddAt = useCallback((lat, lon) => {
    const matches = PlaceFields.nearbyPlaces({ lat, lon }, places || []);
    if (matches.length === 0) {
      createAt(lat, lon);
      return;
    }
    setNearby({ lat, lon, matches });
  }, [places, createAt]);

  return (
    <div className="pb-2">
      <Container className="pt-3">
        <h3 className="mb-0">Places</h3>
        <p className="text-body-secondary small mb-0">
          The shared place database behind waypoint names.{' '}
          <Link to={urlForAllTrips()}>Back to Trips</Link>
        </p>
      </Container>

      <StatusMessage />

      {places && (
        <Suspense fallback={<Spinner />}>
          <PlacesMap
            places={places}
            selectedId={selected?.id || null}
            selectedLngLat={selected?.lngLat || null}
            onSelect={handleSelect}
            onEdit={handleEdit}
            onVisits={handleVisits}
            onAddAt={handleAddAt}
          />
        </Suspense>
      )}

      {/*
        The same rows as the map, as a list you can read. Not lazy -- it reaches
        no maplibre, so it costs the entry bundle nothing.

        Gated on there being rows: an empty gazetteer is already answered by the
        map card's EmptyState, and a second empty card under it would say the
        same thing twice. The card takes no `belowText` on purpose -- the map
        card's "N places" is the page's one count, and a second copy of that
        string would make it ambiguous to find.
      */}
      {places && places.length > 0 && (
        <DataCard
          header={<CardHeader text="All Places" />}
          body={<PlacesTable places={places} onSelect={handleEdit} />}
        />
      )}

      {/*
        Different wording from the trip side's default, because the alternative
        to creating is different: there a waypoint BINDS to the existing row,
        here there is nothing to bind -- the useful thing to offer a curator who
        is about to add a duplicate is the row they already have, open for
        editing.
      */}
      <NearbyPlacesModal
        show={Boolean(nearby)}
        places={nearby?.matches || []}
        title="There is already a place here"
        intro={(count) =>
          count === 1
            ? 'One place is already within half a mile of that spot:'
            : `${count} places are already within half a mile of that spot:`
        }
        useLabel="Edit"
        createLabel="Add a new place here"
        onUse={(place) => {
          setNearby(null);
          handleEdit(place.id);
        }}
        onCreate={() => {
          const tap = nearby;
          setNearby(null);
          if (tap) createAt(tap.lat, tap.lon);
        }}
        onHide={() => setNearby(null)}
      />
    </div>
  );
};

export default Places;
